import { useEffect, useState, type ReactNode } from 'react';
import { CloudAuthError, getCurrentUser, isCloudEnabled, sync, type ClientPrincipal } from '../storage/cloudSync';
import {
  createInvite,
  fetchStoreCatalog,
  listAccounts,
  logoutPasskey,
  setFullAccess,
  startCheckout,
  type AccountSummary,
} from '../auth/passkey';
import { listConflicts, type ConflictRecord } from '../storage/conflictRepository';
import { SyncConflictDialog } from './SyncConflictDialog';
import { useStore } from '../state/store';
import { isIos, isStandalone, checkForPwaUpdate } from '../pwa/register';
import { APP_VERSION, appBuildId } from '../appVersion';

/**
 * アカウント・同期・サインアウト。
 * エミュレータ本体の操作設定は SettingsScreen（Retro → 設定）へ分離。
 */
export function MyScreen() {
  const setView = useStore((s) => s.setView);
  const setSessionUser = useStore((s) => s.setSessionUser);
  const toast = useStore((s) => s.toast);
  const refreshConflicts = useStore((s) => s.refreshConflicts);

  const [user, setUser] = useState<ClientPrincipal | null>(null);
  const [conflicts, setConflicts] = useState<ConflictRecord[]>([]);
  const [showConflicts, setShowConflicts] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [authBusy, setAuthBusy] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [lastInviteUrl, setLastInviteUrl] = useState<string | null>(null);
  const [storeGames, setStoreGames] = useState<Array<{ id: string; title: string; purchasable: boolean }>>([]);
  const [stripeOn, setStripeOn] = useState(false);
  const [accounts, setAccounts] = useState<AccountSummary[]>([]);
  const [grantName, setGrantName] = useState('');
  const isAdmin = Boolean(user?.role === 'admin' || user?.userRoles?.includes('admin'));
  const unlocked = Boolean(user?.fullAccess || isAdmin);

  const reload = () => {
    void listConflicts().then(setConflicts);
    void getCurrentUser().then((u) => {
      setUser(u);
      const admin = Boolean(u?.role === 'admin' || u?.userRoles?.includes('admin'));
      if (!admin) {
        setAccounts([]);
        return;
      }
      void listAccounts()
        .then(setAccounts)
        .catch(() => setAccounts([]));
    });
    void fetchStoreCatalog().then((c) => {
      setStoreGames(c.games);
      setStripeOn(c.stripeConfigured);
    });
  };

  useEffect(() => {
    reload();
    const params = new URLSearchParams(window.location.search);
    const billing = params.get('billing');
    if (billing === 'ok') {
      toast('支払いを受け付けました。枠の反映まで少し時間がかかることがあります。', 'success');
    } else if (billing === 'cancel') {
      toast('支払いはキャンセルされました', 'error');
    }
    if (billing) {
      params.delete('billing');
      const q = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${q ? `?${q}` : ''}${window.location.hash}`);
    }
  }, [toast]);

  const runSync = async () => {
    setSyncing(true);
    try {
      const r = await sync();
      reload();
      void refreshConflicts();
      void useStore.getState().refreshRoms();

      if (r.conflicts > 0) {
        toast(`${r.conflicts} 件の競合があります。内容を確認してください。`, 'error');
        setShowConflicts(true);
      } else {
        const parts: string[] = [];
        if (r.libraryImported) parts.push(`ライブラリ ${r.libraryImported} 件`);
        if (r.uploaded) parts.push(`${r.uploaded} 件送信`);
        if (r.downloaded) {
          const labels = r.downloadedLabels.slice(0, 3);
          if (labels.length > 0) {
            const more =
              r.downloadedLabels.length > 3 ? `ほか ${r.downloadedLabels.length - 3} 件` : '';
            parts.push(more ? `${labels.join('、')}、${more}` : labels.join('、'));
          } else {
            parts.push(`${r.downloaded} 件受信`);
          }
        }
        if (r.skipped) parts.push(`${r.skipped} 件は ROM 未投入のため保留`);
        toast(parts.length ? `${parts.join('、')}を同期しました` : '同期済みです', 'success');
      }

      for (const err of r.errors.slice(0, 3)) {
        toast(`${err.path}: ${err.message}`, 'error');
      }
    } catch (e) {
      toast(
        e instanceof CloudAuthError ? 'サインインしてください' : e instanceof Error ? e.message : String(e),
        'error',
      );
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <header className="pt-safe-header shrink-0 border-b border-base-700 bg-[#07090f] px-4 pb-3 md:hidden landscape:hidden">
        <h1 className="text-base font-semibold">My</h1>
      </header>

      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-4 pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))] pt-4 md:pb-16 landscape:pb-16">
        <Section title="アカウント" hint="サインイン必須。セーブとライブラリ一覧はサーバーに同期されます。">
          {user ? (
            <>
              <p className="text-xs text-slate-400">
                サインイン中: {user.userDetails}
                {user.username ? ` (${user.username})` : ''}
                <br />
                権限: {isAdmin ? '管理者' : '一般'} / 方式: {user.identityProvider}
              </p>
              <button
                type="button"
                className="btn-ghost w-full"
                disabled={authBusy}
                onClick={() => {
                  void (async () => {
                    setAuthBusy(true);
                    try {
                      await logoutPasskey();
                      setUser(null);
                      setSessionUser(null);
                      toast('サインアウトしました', 'success');
                    } catch (e) {
                      toast(e instanceof Error ? e.message : String(e), 'error');
                    } finally {
                      setAuthBusy(false);
                    }
                  })();
                }}
              >
                サインアウト
              </button>

              {isAdmin && (
                <div className="space-y-2 rounded-lg border border-base-700 bg-base-900/50 p-3">
                  <p className="text-xs font-medium text-slate-300">ユーザーを招待</p>
                  <input
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    type="email"
                    placeholder="invitee@example.com"
                    className="w-full rounded-lg border border-base-600 bg-base-900 px-3 py-2 text-sm text-slate-100"
                  />
                  <button
                    type="button"
                    className="btn-primary w-full"
                    disabled={authBusy || !inviteEmail.trim()}
                    onClick={() => {
                      void (async () => {
                        setAuthBusy(true);
                        try {
                          const r = await createInvite(inviteEmail.trim());
                          setLastInviteUrl(r.inviteUrl);
                          if (r.emailSent) {
                            toast(`${r.email} に招待メールを送りました`, 'success');
                          } else {
                            toast(
                              r.emailConfigured
                                ? `メール送信に失敗しました。リンクを共有してください`
                                : `メール未設定のため、招待リンクを共有してください`,
                              'error',
                            );
                          }
                        } catch (e) {
                          toast(e instanceof Error ? e.message : String(e), 'error');
                        } finally {
                          setAuthBusy(false);
                        }
                      })();
                    }}
                  >
                    招待を作成
                  </button>
                  {lastInviteUrl && (
                    <p className="break-all text-[11px] text-slate-400">
                      招待リンク:
                      <br />
                      <a className="text-sky-400 underline" href={lastInviteUrl}>
                        {lastInviteUrl}
                      </a>
                    </p>
                  )}
                  <p className="pt-2 text-xs font-medium text-slate-300">全機能を付ける</p>
                  <p className="text-[11px] leading-relaxed text-slate-500">
                    管理者は常に全機能を使えます。指定したユーザーも、購入なしで追加のサーバー保存と販売ゲームを使えます。
                  </p>
                  <input
                    value={grantName}
                    onChange={(e) => setGrantName(e.target.value)}
                    placeholder="user@example.com"
                    className="w-full rounded-lg border border-base-600 bg-base-900 px-3 py-2 text-sm text-slate-100"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="btn-primary flex-1"
                      disabled={authBusy || !grantName.trim()}
                      onClick={() => {
                        void (async () => {
                          setAuthBusy(true);
                          try {
                            await setFullAccess(grantName.trim(), true);
                            setGrantName('');
                            setAccounts(await listAccounts());
                            toast('全機能を付けました', 'success');
                          } catch (e) {
                            toast(e instanceof Error ? e.message : String(e), 'error');
                          } finally {
                            setAuthBusy(false);
                          }
                        })();
                      }}
                    >
                      付ける
                    </button>
                    <button
                      type="button"
                      className="btn-ghost flex-1"
                      disabled={authBusy || !grantName.trim()}
                      onClick={() => {
                        void (async () => {
                          setAuthBusy(true);
                          try {
                            await setFullAccess(grantName.trim(), false);
                            setGrantName('');
                            setAccounts(await listAccounts());
                            toast('全機能を外しました', 'success');
                          } catch (e) {
                            toast(e instanceof Error ? e.message : String(e), 'error');
                          } finally {
                            setAuthBusy(false);
                          }
                        })();
                      }}
                    >
                      外す
                    </button>
                  </div>
                  {accounts.filter((a) => a.role !== 'admin').length > 0 && (
                    <ul className="space-y-1 text-[11px] text-slate-400">
                      {accounts
                        .filter((a) => a.role !== 'admin')
                        .map((a) => (
                          <li key={a.username} className="flex items-center justify-between gap-2">
                            <span className="truncate">
                              {a.displayName} ({a.username})
                            </span>
                            <button
                              type="button"
                              className="shrink-0 text-sky-400 underline"
                              disabled={authBusy}
                              onClick={() => {
                                void (async () => {
                                  setAuthBusy(true);
                                  try {
                                    await setFullAccess(a.username, !a.fullAccess);
                                    setAccounts(await listAccounts());
                                    toast(a.fullAccess ? '全機能を外しました' : '全機能を付けました', 'success');
                                  } catch (e) {
                                    toast(e instanceof Error ? e.message : String(e), 'error');
                                  } finally {
                                    setAuthBusy(false);
                                  }
                                })();
                              }}
                            >
                              {a.fullAccess ? '全機能を外す' : '全機能を付ける'}
                            </button>
                          </li>
                        ))}
                    </ul>
                  )}
                </div>
              )}
            </>
          ) : (
            <p className="text-xs text-slate-400">セッションを確認しています…</p>
          )}
        </Section>

        {isCloudEnabled() && (
          <Section
            title="サーバー同期"
            hint={
              unlocked
                ? 'セーブとライブラリ一覧を同期します。オートセーブは端末内のみです。このアカウントはサーバーの保存枠を購入せずに使えます。端末内の保存も制限しません。'
                : 'セーブとライブラリ一覧を同期します。オートセーブは端末内のみです。サーバーの無料枠は、カートリッジ機がゲーム内セーブ1つとステート1つ、PS1 がメモリーカード1枚、PSP がゲーム内セーブ1つです。それを超える分は購入が必要です。端末内の保存は制限しません。'
            }
          >
            {user ? (
              <>
                {conflicts.length > 0 && (
                  <button type="button" className="btn-danger w-full" onClick={() => setShowConflicts(true)}>
                    ⚠ 未解決の競合 {conflicts.length} 件を確認
                  </button>
                )}
                <button type="button" className="btn-ghost w-full" disabled={syncing} onClick={() => void runSync()}>
                  {syncing ? '同期中…' : '今すぐ同期'}
                </button>
              </>
            ) : (
              <p className="text-xs text-slate-400">同期するにはサインインが必要です。</p>
            )}
          </Section>
        )}

        <Section
          title="追加の保存と販売ゲーム"
          hint={
            unlocked
              ? 'このアカウントは購入なしで、追加のサーバー保存と販売ゲームを使えます。'
              : '遊ぶこと自体は無料です。サーバーのステート追加と PS1 の 2 枚目以降のカード、販売ゲームだけ支払いが必要です。'
          }
        >
          {user && unlocked ? (
            <>
              <p className="text-xs text-slate-400">
                {isAdmin ? '管理者のため、購入は不要です。' : '管理者から全機能が付いています。購入は不要です。'}
              </p>
              {storeGames.map((game) => (
                <p key={game.id} className="text-xs text-slate-300">
                  {game.title}（利用できます）
                </p>
              ))}
            </>
          ) : user ? (
            <>
              <p className="text-xs text-slate-400">
                追加済みのステート枠: {user.extraStateSlots ?? 0} / PS1 追加カード: {user.extraPsxCards ?? 0}
              </p>
              <button
                type="button"
                className="btn-ghost w-full"
                disabled={authBusy || !stripeOn}
                onClick={() => {
                  void startCheckout('extra-state').catch((e) =>
                    toast(e instanceof Error ? e.message : String(e), 'error'),
                  );
                }}
              >
                セーブステートのサーバー枠を 1 つ買う
              </button>
              <button
                type="button"
                className="btn-ghost w-full"
                disabled={authBusy || !stripeOn}
                onClick={() => {
                  void startCheckout('extra-psx-card').catch((e) =>
                    toast(e instanceof Error ? e.message : String(e), 'error'),
                  );
                }}
              >
                PS1 メモリーカードを 1 枚買う
              </button>
              {storeGames.map((game) => {
                const owned = user.ownedGameIds?.includes(game.id);
                return (
                  <button
                    key={game.id}
                    type="button"
                    className="btn-ghost w-full"
                    disabled={authBusy || owned || !game.purchasable}
                    onClick={() => {
                      void startCheckout('game', game.id).catch((e) =>
                        toast(e instanceof Error ? e.message : String(e), 'error'),
                      );
                    }}
                  >
                    {owned ? `${game.title}（購入済み）` : `${game.title} を買う`}
                  </button>
                );
              })}
              {!stripeOn && (
                <p className="text-xs text-slate-500">決済キーが未設定のため、購入ボタンはまだ使えません。</p>
              )}
            </>
          ) : (
            <p className="text-xs text-slate-400">サインインすると購入できます。</p>
          )}
        </Section>

        <Section title="エミュレーター">
          <p className="text-xs text-slate-400">パッド・表示・BIOS・メモリーカードなどは Retro の設定から変更できます。</p>
          <button type="button" className="btn-ghost w-full" onClick={() => setView('settings')}>
            エミュレーター設定を開く
          </button>
        </Section>

        {isIos() && !isStandalone() && (
          <Section title="ホーム画面に追加">
            <p className="text-xs leading-relaxed text-slate-400">
              Safari 下部の <strong>共有ボタン</strong> → <strong>「ホーム画面に追加」</strong> を選ぶと、全画面で動作します。
            </p>
          </Section>
        )}

        <div className="space-y-2 pt-4">
          <button
            type="button"
            className="btn-ghost w-full text-xs"
            onClick={() => {
              void (async () => {
                const r = await checkForPwaUpdate();
                if (r === 'unsupported') {
                  toast('この環境ではオフライン更新を確認できません', 'error');
                  return;
                }
                toast('更新を確認しました。新しい版があれば画面下に案内が出ます。', 'info');
              })();
            }}
          >
            アプリの更新を確認
          </button>
          <p className="select-text text-center text-[0.65rem] leading-relaxed text-slate-600">
            version {APP_VERSION}
            <br />
            build {appBuildId()}
          </p>
        </div>
      </div>

      {showConflicts && conflicts.length > 0 && (
        <SyncConflictDialog
          conflicts={conflicts}
          onClose={() => setShowConflicts(false)}
          onResolved={() => {
            reload();
            void refreshConflicts();
          }}
        />
      )}
    </div>
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="space-y-2.5">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">{title}</h2>
      {hint && <p className="text-[0.7rem] leading-relaxed text-slate-600">{hint}</p>}
      {children}
    </section>
  );
}
